import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  PageActionStrip,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  CoaComparisonCard,
  OkrAlignmentTree,
  PredictiveSlippage,
  AlertCard,
  PageViewerControls,
  KpiHeroBand,
  StaggerReveal,
  BrandedEmptyState,
  toast,
  activityRail,
} from '../../components/index.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import './decision.css';
import './decision.redesign.css';

const RISK_TONE = {
  neutral: 'sage',
  moderate: 'amber',
  high: 'amber',
  critical: 'brick',
  reduced: 'sage',
};

// Only criteria backed by real `/decisions/coas` payload fields are listed.
// The legacy `risk` and `complexity` rows read fields (riskProfile,
// integrationComplexity) that are not in the seed payload and only rendered a
// fabricated "standard" chip, so they are replaced with the real platform
// `type` and `compliancePosture` data.
const CRITERIA = [
  { key: 'cost', label: 'Implementation cost', format: 'value' },
  { key: 'timeline', label: 'Timeline to IOC', format: 'value' },
  { key: 'alignment', label: 'Weighted decision score', format: 'bar' },
  { key: 'platform', label: 'Platform approach', format: 'chip' },
  { key: 'compliance', label: 'Compliance posture', format: 'chip' },
];

function coaForUi(c) {
  const cost = c.estimatedCostUsdM ?? c.costUsdM ?? c.forecastDeltaUsdM;
  // Real seed fields are months-to-pilot / months-to-IOC. Prefer an explicit
  // description, then derive a readable timeline from the real month fields
  // (the page previously read a non-existent `timelineWeeks` and always
  // rendered "—").
  const iocMonths = c.timelineMonthsToIoc ?? c.timelineMonthsToIOC;
  const pilotMonths = c.timelineMonthsToPilot;
  const timeline =
    c.timelineDescription ||
    (iocMonths != null
      ? `${iocMonths} mo to IOC`
      : pilotMonths != null
        ? `${pilotMonths} mo to pilot`
        : c.timelineWeeks
          ? `${c.timelineWeeks} weeks`
          : '—');
  // Alignment bar reflects the real weighted decision score (0–100). The old
  // fallback to a literal 60 was placeholder data; we now use the actual
  // `weightedTotalScore` and only fall back to other real score fields.
  const alignment = c.weightedTotalScore ?? c.strategicAlignmentScore ?? c.missionImpact ?? null;
  return {
    ...c,
    _scoreNum: alignment != null ? Math.round(Number(alignment)) : null,
    _rank: c.rank ?? null,
    _iocMonths: iocMonths ?? null,
    _costUsdM: cost != null ? Number(cost) : null,
    criteria: {
      cost: { value: cost != null ? `$${Number(cost).toFixed(1)} M` : '—' },
      timeline: { value: timeline },
      alignment: {
        value: alignment != null ? Math.round(Number(alignment)) : 0,
        tone:
          alignment == null ? '' : alignment >= 75 ? 'sage' : alignment >= 65 ? 'amber' : 'brick',
      },
      platform: {
        value: humanizeToken(c.type) || '—',
        label: humanizeToken(c.type) || '—',
        // Custom-native builds carry more delivery risk than extending an
        // existing enterprise platform — tone reflects that real tradeoff.
        tone: /custom|native|greenfield/i.test(c.type || '')
          ? 'amber'
          : /existing|enterprise|internal/i.test(c.type || '')
            ? 'sage'
            : 'blue',
      },
      compliance: {
        value: c.compliancePosture || 'standard',
        label: c.compliancePosture || 'standard',
        tone: /strong|verified|authorized/i.test(c.compliancePosture || '')
          ? 'sage'
          : /requires|review|pending|boundary/i.test(c.compliancePosture || '')
            ? 'amber'
            : /promising/i.test(c.compliancePosture || '')
              ? 'blue'
              : 'blue',
      },
    },
  };
}

// Overall card-rail tone from the COA's real standing: a recommended option
// reads blue (mission-control accent), otherwise tone follows the weighted
// decision score band. Returns 'slate' when no score is available.
function coaRailTone(coa) {
  if (coa?.recommended) return 'blue';
  const s = coa?._scoreNum;
  if (s == null) return 'slate';
  return s >= 75 ? 'sage' : s >= 65 ? 'amber' : 'brick';
}

// Turn a kebab/snake token like "existing-enterprise-platform" into
// "Existing enterprise platform" for boardroom-legible chips.
function humanizeToken(token) {
  if (!token || typeof token !== 'string') return '';
  const spaced = token.replace(/[-_]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function StrategicDecisionWorkspacePage() {
  const [params] = useSearchParams();
  const focusScope = params.get('coaScope');
  const coas = useApi('/decisions/coas', {
    query: focusScope ? { scopeId: focusScope, limit: 100 } : { limit: 100 },
  });
  const scenarios = useApi('/scenarios', { query: { limit: 50 } });
  const objectivesRes = useApi('/objectives', { query: { limit: 50 } });
  const projectsRes = useApi('/projects', { query: { limit: 100 } });
  const [selectedId, setSelectedId] = useState(null);
  const [rationale, setRationale] = useState('');
  const [scenarioPct, setScenarioPct] = useState(15);
  const [scenarioResult, setScenarioResult] = useState(null);
  const runScenarioMut = useMutation();
  const saveRationale = useMutation();
  const { user } = useCurrentUser();
  const debounceRef = useRef(null);

  const coaListRaw = useMemo(() => (coas.data?.data || []).map(coaForUi), [coas.data]);
  // Single authoritative recommendation (F-G05-002): the API service is the primary guard, but
  // the page also collapses any accidental multi-flag payload so exactly one card wears the
  // ribbon and the banner + footer agree with it. First stored-bit COA wins.
  const recommendedId = useMemo(
    () => coaListRaw.find((c) => c.recommended)?.id ?? null,
    [coaListRaw],
  );
  const coaList = useMemo(
    () => coaListRaw.map((c) => ({ ...c, recommended: c.id === recommendedId })),
    [coaListRaw, recommendedId],
  );
  const recommended = useMemo(() => coaList.find((c) => c.recommended), [coaList]);
  const selectedCoa = useMemo(
    () => coaList.find((c) => c.id === selectedId) || recommended || coaList[0],
    [coaList, selectedId, recommended],
  );
  const coaPager = useClientPagination(coaList, {
    initialPageSize: 8,
    resetKey: focusScope || 'all',
  });

  // KPI hero band — every tile is derived from the real COA payload already
  // fetched (no fabricated numbers). Tiles are omitted when their backing data
  // is absent so the band never invents a value.
  const kpiItems = useMemo(() => {
    if (!coaList.length) return [];
    const costed = coaList.filter((c) => c._costUsdM != null);
    const timed = coaList.filter((c) => c._iocMonths != null);
    const lead = recommended || selectedCoa || coaList[0];
    const items = [];

    items.push({
      id: 'options',
      label: 'Options on the table',
      value: coaList.length,
      unit: coaList.length === 1 ? 'COA' : 'COAs',
      status: 'blue',
      statusLabel: 'Evaluated',
      footer: recommended
        ? `1 AI-recommended · ${coaList.length - 1} alternative${coaList.length - 1 === 1 ? '' : 's'}`
        : 'Awaiting recommendation',
    });

    if (lead?._scoreNum != null) {
      const tone = lead._scoreNum >= 75 ? 'sage' : lead._scoreNum >= 65 ? 'amber' : 'brick';
      items.push({
        id: 'lead-score',
        label: recommended ? 'Recommended COA score' : 'Lead COA score',
        value: lead._scoreNum,
        unit: '/ 100',
        status: tone,
        statusLabel: lead._rank != null ? `Rank #${lead._rank}` : 'Weighted',
        footer: lead.name || lead.id,
      });
    }

    if (costed.length) {
      const min = costed.reduce((a, b) => (b._costUsdM < a._costUsdM ? b : a));
      items.push({
        id: 'low-cost',
        label: 'Lowest implementation cost',
        value: `$${min._costUsdM.toFixed(1)}`,
        unit: 'M',
        status: 'sage',
        statusLabel: 'Floor',
        footer: min.name || min.id,
      });
    }

    if (timed.length) {
      const fast = timed.reduce((a, b) => (b._iocMonths < a._iocMonths ? b : a));
      items.push({
        id: 'fast-ioc',
        label: 'Fastest path to IOC',
        value: fast._iocMonths,
        unit: 'mo',
        status: 'blue',
        statusLabel: 'Earliest',
        footer: fast.name || fast.id,
      });
    }

    return items;
  }, [coaList, recommended, selectedCoa]);

  const objective = (objectivesRes.data?.data || [])[0] || null;
  const keyResults = useMemo(() => {
    if (!objective) return [];
    const krs = objective.keyResults || objective.krs || [];
    return krs.slice(0, 4).map((kr) => ({
      ...kr,
      id: kr.id || kr.name,
      name: kr.name || kr.title,
      progress: Math.round(kr.progress || kr.currentValue || 0),
      target: kr.target || kr.targetValue,
    }));
  }, [objective]);
  // Real OKR→project linkage from the objective payload (alignedProjectIds in
  // the seed corpus; relatedProjectIds accepted for compatibility). Only when
  // the payload carries no links do we fall back to the name-based heuristic —
  // and then the section is explicitly labeled as a suggestion (TT-12c).
  const alignedProjectIds = useMemo(() => {
    const ids = objective?.alignedProjectIds || objective?.relatedProjectIds;
    return Array.isArray(ids) && ids.length ? ids : null;
  }, [objective]);
  const projects = useMemo(() => {
    return (projectsRes.data?.data || []).slice(0, 4).map((p) => ({
      id: p.id,
      name: p.name,
      status: p.status,
      richTooltip: p.richTooltip || null,
      linked: alignedProjectIds
        ? alignedProjectIds.includes(p.id)
        : /sentinel|diagnostic|virtual|ai/i.test(p.name),
      healthTone:
        (p.healthScores?.overall ?? 70) >= 75
          ? 'sage'
          : (p.healthScores?.overall ?? 70) >= 60
            ? 'amber'
            : 'brick',
    }));
  }, [projectsRes.data, alignedProjectIds]);

  // Chart data comes exclusively from the server run payload (TT-12b). Before
  // the first run completes the panel shows an explicit no-data state instead
  // of a client-computed curve.
  const scenarioData = useMemo(
    () =>
      Array.isArray(scenarioResult?.forecastCurve) && scenarioResult.forecastCurve.length
        ? scenarioResult.forecastCurve
        : null,
    [scenarioResult],
  );

  async function runScenario({ silent = false } = {}) {
    try {
      const target =
        (scenarios.data?.data || []).find((s) => s.coaId === selectedCoa?.id) ||
        (scenarios.data?.data || [])[0];
      if (!target) {
        if (!silent) {
          toast.warn({ title: 'No scenario available', body: 'Seed scenarios are not loaded.' });
        }
        return;
      }
      const res = await runScenarioMut.run(() =>
        api.post(`/scenarios/${target.id}/run`, {
          budgetCutPercent: scenarioPct,
          coaId: selectedCoa?.id,
        }),
      );
      const data = res.data || res;
      setScenarioResult(data);
      if (!silent) {
        toast.success({
          title: `Scenario · ${scenarioPct}% cut`,
          body: `Forecast Δ $${data.forecastDeltaUsdM?.toFixed?.(1) ?? data.forecastDeltaUsdM ?? '—'}M · risk ${data.riskShift ?? '—'}`,
        });
        activityRail.push({
          action: 'scenario.run',
          targetType: 'scenario',
          targetId: target.id,
          actorUserId: user?.id || 'unknown',
          summary: `${scenarioPct}% cut on ${selectedCoa?.id || target.id} → ${data.riskShift}`,
        });
      }
    } catch (e) {
      if (!silent) toast.error({ title: 'Scenario run failed', body: e.message });
    }
  }

  // Debounced auto-run on slider change so the output panel feels live without
  // hammering the API. The explicit Run button still fires immediately.
  useEffect(() => {
    if (!selectedCoa) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runScenario({ silent: true });
    }, 650);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioPct, selectedCoa?.id]);

  async function recordRationale() {
    if (!selectedCoa || !rationale.trim()) return;
    try {
      await saveRationale.run(() =>
        api.post('/saved-views', {
          name: `Decision rationale — ${selectedCoa.name || selectedCoa.id}`,
          routePath: '/decision-support',
          scope: 'decision-rationale',
          filters: { coaId: selectedCoa.id, rationale, decidedAt: new Date().toISOString() },
        }),
      );
      setRationale('');
      toast.success({
        title: 'Rationale recorded',
        body: `${selectedCoa.id} · stored under saved views with audit trail.`,
      });
      activityRail.push({
        action: 'decision.rationale.captured',
        targetType: 'coa',
        targetId: selectedCoa.id,
        actorUserId: user?.id || 'unknown',
        summary: rationale.slice(0, 96),
      });
    } catch (e) {
      toast.error({ title: 'Could not save rationale', body: e.message });
    }
  }

  if (coas.loading) return <LoadingSkeleton rows={6} />;
  if (coas.error) return <ErrorState error={coas.error} />;
  if (!coaList.length) return <EmptyState title="No COAs available" />;

  return (
    <>
      {/* PRIME-SHELL-UX-001: single body page identity + relocated About. */}
      <PageHeader />
      <PageActionStrip
        actions={
          <button className="btn ghost" onClick={runScenario} disabled={runScenarioMut.running}>
            {runScenarioMut.running ? 'Running…' : 'Run scenario'}
          </button>
        }
      />
      {kpiItems.length ? (
        <KpiHeroBand
          className="decision-kpi-band"
          eyebrow="Decision posture"
          asOf={coas.loadedAt}
          items={kpiItems}
          columns={Math.min(4, kpiItems.length)}
        />
      ) : null}
      {recommended ? (
        <AlertCard
          severity="blue"
          title={`Recommended COA: ${recommended.name || recommended.title}`}
          body={
            recommended.summary ||
            recommended.description ||
            'AI-assisted recommendation. Capture rationale below before approving.'
          }
          meta={`${recommended.id} · pending human decision`}
        />
      ) : null}

      <StaggerReveal as="section" className="bento decision-bento">
        <div className="span-12">
          <div className="coa-grid">
            {coaPager.items.map((c) => {
              const tone = coaRailTone(c);
              return (
                <div key={c.id} className="coa-tone-wrap" data-tone={tone}>
                  {c._scoreNum != null || c._rank != null ? (
                    <div className="coa-tone-badges">
                      {c._scoreNum != null ? (
                        <span className="coa-score-badge">
                          <span className="coa-score-num">{c._scoreNum}</span>
                          <span className="coa-score-unit">/ 100</span>
                        </span>
                      ) : null}
                      {c._rank != null ? (
                        <span className="coa-rank-chip">Rank #{c._rank}</span>
                      ) : null}
                    </div>
                  ) : null}
                  <CoaComparisonCard
                    coa={c}
                    criteria={CRITERIA}
                    isSelected={selectedCoa?.id === c.id}
                    onSelect={() => setSelectedId(c.id)}
                  />
                </div>
              );
            })}
          </div>
          <PageViewerControls
            page={coaPager.page}
            label="COAs"
            pageSize={coaPager.pageSize}
            onOffsetChange={coaPager.setOffset}
            onPageSizeChange={coaPager.setPageSize}
          />
        </div>

        <div className="span-8 card decision-scenario">
          <header className="row spread">
            <div>
              <div className="eyebrow">Predictive risk model</div>
              <h2 className="card-title">What if the budget is cut?</h2>
              <p className="muted">
                Adjust the slider to model timeline impact and mission outcome shift.
              </p>
            </div>
          </header>
          <div className="scenario-slider-wrap">
            <label className="row" style={{ gap: 12 }}>
              <span className="eyebrow">Budget cut</span>
              <input
                type="range"
                min={0}
                max={30}
                step={1}
                value={scenarioPct}
                onChange={(e) => setScenarioPct(Number(e.target.value))}
              />
              <span className="mono" style={{ color: 'var(--status-brick)', fontWeight: 700 }}>
                {scenarioPct}%
              </span>
            </label>
          </div>
          {scenarioData?.length ? (
            <>
              <PredictiveSlippage
                data={scenarioData}
                baselineLabel="Baseline plan"
                predictedLabel="With cut"
                unit=" wks"
                height={300}
              />
              <div className="scenario-legend" aria-hidden>
                <span className="leg-baseline">
                  <i /> Baseline plan
                </span>
                <span className="leg-predicted">
                  <i /> With {scenarioPct}% cut
                </span>
              </div>
            </>
          ) : (
            <div className="scenario-forecast-empty">
              <BrandedEmptyState
                eyebrow="Predictive model"
                title="No scenario data"
                description="Run a scenario to project timeline slippage. The curve is drawn only from the server model's run output — nothing is estimated in the browser."
                tone="blue"
              />
            </div>
          )}
          {scenarioResult ? (
            <div className="scenario-output" data-testid="scenario-output">
              <div className="scenario-output-head">
                <span className={`chip ${RISK_TONE[scenarioResult.riskShift] || 'blue'}`}>
                  {scenarioResult.riskShift?.toUpperCase()} risk
                </span>
                <span className="scenario-delta mono">
                  Forecast Δ $ {Number(scenarioResult.forecastDeltaUsdM ?? 0).toFixed(1)}M
                </span>
              </div>
              <p className="scenario-summary">{scenarioResult.summary}</p>
              <p className="scenario-recommendation">
                <strong>Recommendation:</strong> {scenarioResult.recommendation}
              </p>
              <AffectedProjectsList projects={scenarioResult.affectedProjects || []} />
            </div>
          ) : null}
        </div>

        <div className="span-4 card decision-rationale">
          <header>
            <div className="eyebrow">Decision capture</div>
            <h2 className="card-title">Record the rationale</h2>
            <p className="muted">
              {selectedCoa
                ? `Selected: ${selectedCoa.name || selectedCoa.id}`
                : 'Select a COA card above to begin.'}
            </p>
          </header>
          <textarea
            rows={6}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder={
              selectedCoa
                ? 'Why select this COA? What tradeoffs did the panel accept?'
                : 'Select a COA first…'
            }
            disabled={!selectedCoa}
          />
          <button
            className="btn primary tactile-press"
            onClick={recordRationale}
            disabled={!selectedCoa || !rationale.trim() || saveRationale.running}
          >
            {saveRationale.running ? 'Saving…' : 'Record rationale'}
          </button>
          <p className="muted small">
            Stored as a saved view and emitted to the audit log as{' '}
            <span className="mono">decision.rationale.captured</span>.
          </p>
        </div>

        <div className="span-12 card decision-okrs">
          <header className="row spread">
            <div>
              <div className="eyebrow">Strategic alignment</div>
              <h2 className="card-title">Objective → Key Results → Projects</h2>
            </div>
            {objective && !alignedProjectIds ? (
              <span
                className="chip amber sm"
                title="The objective payload carries no project links; alignment shown here is inferred from project names."
              >
                Suggested matches (name-based)
              </span>
            ) : null}
          </header>
          {objective ? (
            <OkrAlignmentTree
              objective={{
                name: objective.name || objective.title,
                summary: objective.summary || objective.description,
                targetDate: objective.targetDate,
              }}
              keyResults={keyResults}
              projects={projects}
            />
          ) : (
            <EmptyState title="No objectives loaded" />
          )}
        </div>
      </StaggerReveal>
    </>
  );
}

function AffectedProjectsList({ projects }) {
  const pager = useClientPagination(projects, {
    initialPageSize: 5,
    resetKey: `affected-${projects.length}`,
  });
  if (!projects.length) return null;
  return (
    <>
      <ul className="scenario-affected">
        {pager.items.map((p) => (
          <li key={p.id}>
            <span className="mono">{p.id}</span>
            <span className="muted small">
              {' · '}Δ ${Number(p.forecastDeltaUsdM).toFixed(1)}M · {p.riskDeltaTier}
            </span>
          </li>
        ))}
      </ul>
      <PageViewerControls
        page={pager.page}
        label="affected projects"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </>
  );
}
